import type { Item, ServerMessage } from '@slipway/shared';
import type { Api } from './api.js';

export type ConnState = 'offline' | 'connecting' | 'online';

/**
 * Keeps the browser in step with the server.
 *
 * The socket is only an accelerator. Correctness comes from `catchUp()`, which
 * runs before the socket is trusted and again after every reconnect — so a
 * dropped frame, a sleeping laptop, or a hibernated Durable Object can delay an
 * item but can never lose one.
 */
export class SyncEngine {
  private ws: WebSocket | null = null;
  private cursor: string | null = null;
  private attempt = 0;
  private timer: number | null = null;
  private heartbeat: number | null = null;
  private stopped = false;

  constructor(
    private api: Api,
    private handlers: {
      onItems: (items: Item[]) => void;
      onState: (s: ConnState) => void;
      onRevoked: () => void;
    },
  ) {}

  get cursorValue(): string | null {
    return this.cursor;
  }

  async start(): Promise<void> {
    this.stopped = false;
    await this.catchUp();
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.timer !== null) clearTimeout(this.timer);
    if (this.heartbeat !== null) clearInterval(this.heartbeat);
    this.ws?.close();
    this.ws = null;
    this.handlers.onState('offline');
  }

  /** Authoritative reconciliation. Safe to call at any time. */
  async catchUp(): Promise<void> {
    try {
      const { items, cursor } = await this.api.sync(this.cursor);
      if (items.length > 0) {
        this.handlers.onItems(items);
        // Receipts are what make "delivered" mean something; fire and forget.
        for (const it of items) void this.api.receipt(it.id).catch(() => {});
      }
      if (cursor) this.cursor = cursor;
    } catch {
      // Network is down; the reconnect loop will retry.
    }
  }

  private connect(): void {
    if (this.stopped) return;
    this.handlers.onState('connecting');

    let ws: WebSocket;
    try {
      ws = new WebSocket(this.api.websocketUrl());
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.handlers.onState('online');
      // A socket that opened may still have missed items while it was down.
      void this.catchUp();

      this.heartbeat = window.setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, 30_000);
    };

    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(ev.data as string) as ServerMessage;
      } catch {
        return;
      }

      if (msg.type === 'item') {
        this.handlers.onItems([msg.item]);
        if (!this.cursor || msg.item.id > this.cursor) this.cursor = msg.item.id;
        void this.api.receipt(msg.item.id).catch(() => {});
      } else if (msg.type === 'device_revoked') {
        if (msg.deviceId === this.api.getSession()?.deviceId) {
          this.stop();
          this.handlers.onRevoked();
        }
      }
    };

    ws.onclose = () => {
      if (this.heartbeat !== null) clearInterval(this.heartbeat);
      this.heartbeat = null;
      this.ws = null;
      this.handlers.onState('offline');
      this.scheduleReconnect();
    };

    ws.onerror = () => ws.close();
  }

  /**
   * Jittered exponential backoff, capped at 30s. The jitter matters: without it
   * every tab an account has open would retry in lockstep after an outage.
   */
  private scheduleReconnect(): void {
    if (this.stopped) return;
    const base = Math.min(1000 * 2 ** this.attempt, 30_000);
    const delay = base * (0.7 + Math.random() * 0.6);
    this.attempt++;
    if (this.timer !== null) clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.connect(), delay);
  }
}

/** Exported for tests: the backoff shape is a reliability property worth pinning. */
export function backoffDelay(attempt: number, rand = 0.5): number {
  const base = Math.min(1000 * 2 ** attempt, 30_000);
  return base * (0.7 + rand * 0.6);
}
